import { useMemo, useState } from 'react';

import { DEFAULT_AVATAR_CONFIG } from '@perplexicon/validation';
import type { AvatarBase, AvatarConfig } from '@perplexicon/validation';

import { Button, Card, ErrorState, StatusMessage } from '../../components/index.js';
import {
  NO_HAIR_STYLE,
  UNIVERSAL_EYE_SHAPE,
  avatarLayerSource,
  hasHairArt,
} from '../../components/avatar/avatar-assets.js';
import {
  BASE_OPTIONS,
  HAIR_COLOR_SWATCHES,
  HAIR_STYLE_OPTIONS,
  SKIN_TONE_SWATCHES,
} from '../../components/avatar/avatar-config.js';
import type { AvatarOption, AvatarSwatch } from '../../components/avatar/avatar-config.js';
import { useCurrentUser, useUpdateAvatar } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';
import { AvatarDisplay } from '../profile/components/AvatarDisplay.js';

/** Rendered width of a base thumbnail, in CSS pixels. */
const BASE_THUMBNAIL_PX = 56;

/**
 * The editor only offers almond eyes for now — the one shape drawn for every
 * base — so drafts are pinned to it. A saved avatar in another shape keeps
 * rendering that shape everywhere else until the player saves again.
 */
const editable = (config: AvatarConfig): AvatarConfig => ({
  ...config,
  eyeShape: UNIVERSAL_EYE_SHAPE,
});

function OptionRow<T extends string>({
  legend,
  options,
  value,
  onChange,
  children,
}: {
  legend: string;
  options: ReadonlyArray<AvatarOption<T>>;
  value: T;
  onChange: (next: T) => void;
  children?: React.ReactNode;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-semibold text-textPrimary">{legend}</legend>
      {children}
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.id === value;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.id)}
              className={`min-h-touch rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-fast ${
                selected
                  ? 'border-accent bg-brand text-textOnBrand shadow-1'
                  : 'border-border bg-surface text-textPrimary hover:bg-surfaceElevated'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function SwatchRow<T extends string>({
  legend,
  swatches,
  value,
  onChange,
}: {
  legend: string;
  swatches: ReadonlyArray<AvatarSwatch<T>>;
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-semibold text-textPrimary">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {swatches.map((swatch) => {
          const selected = swatch.id === value;
          return (
            <button
              key={swatch.id}
              type="button"
              aria-pressed={selected}
              aria-label={swatch.label}
              title={swatch.label}
              onClick={() => onChange(swatch.id)}
              className={`h-11 w-11 rounded-full border-2 transition-transform duration-fast ${
                selected ? 'border-accent scale-110 shadow-1' : 'border-border hover:scale-105'
              }`}
              style={{ backgroundColor: swatch.hex }}
            >
              <span className="sr-only">{swatch.label}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * The bodies as a grid of portrait thumbnails, each drawn in the draft's skin
 * tone so the player compares bodies as they would actually look. Only the
 * body layer is shown — the choice here is the body, not the hair.
 */
function BaseRow({
  legend,
  options,
  draft,
  onChange,
}: {
  legend: string;
  options: ReadonlyArray<AvatarOption<AvatarBase>>;
  draft: AvatarConfig;
  onChange: (next: AvatarBase) => void;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-semibold text-textPrimary">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.id === draft.base;
          const { src, srcSet } = avatarLayerSource('body', { ...draft, base: option.id });
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              aria-label={option.label}
              title={option.label}
              onClick={() => onChange(option.id)}
              className={`h-14 w-14 overflow-hidden rounded-full border-2 bg-surface transition-transform duration-fast ${
                selected ? 'border-accent scale-110 shadow-1' : 'border-border hover:scale-105'
              }`}
            >
              <img
                src={src}
                srcSet={srcSet}
                sizes={`${String(BASE_THUMBNAIL_PX)}px`}
                width={BASE_THUMBNAIL_PX}
                height={BASE_THUMBNAIL_PX}
                alt=""
                loading="lazy"
                decoding="async"
                draggable={false}
                className="h-full w-full"
              />
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * The avatar customization editor (APP_SPEC §13). Shows the saved avatar
 * and a live preview that updates as options change, then persists the
 * whole configuration to the user's profile. Every option list is
 * available regardless of the chosen base — nothing here is gender-locked.
 *
 * The preview is framed with `AvatarDisplay` — the same avatar-frame
 * cosmetic the player picked in Settings > Profile Appearance
 * (`user.customization.avatarFrameId`), not a generic editor chrome — so
 * this screen always shows their avatar exactly as their frame presents it
 * everywhere else (profile hero, appearance preview).
 */
export function AvatarEditor() {
  const { data: user } = useCurrentUser();
  const updateAvatar = useUpdateAvatar();

  const saved: AvatarConfig = editable(user?.avatar ?? DEFAULT_AVATAR_CONFIG);
  const [draft, setDraft] = useState<AvatarConfig>(saved);
  // Re-seed the draft if the saved avatar changes underneath us (e.g. first load).
  const savedKey = JSON.stringify(saved);
  const [seededFrom, setSeededFrom] = useState(savedKey);
  if (savedKey !== seededFrom && !updateAvatar.isPending) {
    setSeededFrom(savedKey);
    setDraft(saved);
  }

  const isDirty = useMemo(() => JSON.stringify(draft) !== savedKey, [draft, savedKey]);

  const set = <K extends keyof AvatarConfig>(key: K, value: AvatarConfig[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col items-center gap-4 tablet:flex-row tablet:items-start">
        <div className="flex flex-col items-center gap-2" data-testid="avatar-preview">
          <AvatarDisplay
            userId={user?.id ?? ''}
            username={user?.username ?? ''}
            avatar={draft}
            frameId={user?.customization.avatarFrameId ?? null}
            size={128}
          />
          <p className="text-xs text-textSecondary">Live preview</p>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-medium text-textPrimary">Your character</h2>
          <p className="mt-1 text-sm text-textSecondary">
            Mix any option freely — hairstyles and colours work with every base.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={() => updateAvatar.mutate(draft)}
              isLoading={updateAvatar.isPending}
              disabled={!isDirty}
            >
              Save avatar
            </Button>
            <Button variant="secondary" onClick={() => setDraft(saved)} disabled={!isDirty}>
              Reset
            </Button>
          </div>
          {updateAvatar.isError && (
            <div className="mt-3">
              <ErrorState message={getErrorMessage(updateAvatar.error)} />
            </div>
          )}
          {updateAvatar.isSuccess && !isDirty && (
            <StatusMessage className="mt-3" tone="success">
              Avatar saved.
            </StatusMessage>
          )}
        </div>
      </Card>

      <Card className="grid gap-5">
        <BaseRow
          legend="Base"
          options={BASE_OPTIONS}
          draft={draft}
          onChange={(v) => set('base', v)}
        />
        <SwatchRow
          legend="Skin tone"
          swatches={SKIN_TONE_SWATCHES}
          value={draft.skinTone}
          onChange={(v) => set('skinTone', v)}
        />
        {/*
          Eye shape is intentionally not offered yet: only almond is drawn for
          every base, so drafts are pinned to it (see `editable` above).
          Restoring the `OptionRow` for `EYE_SHAPE_OPTIONS` here is all that is
          needed once every base has the other shapes.

          Eye colour is not offered either: the eyes are painted into each body
          layer and there is no iris-overlay art to tint them with
          (APP_SPEC §13.1). The field is still stored and round-tripped.
        */}
        <OptionRow
          legend="Hair style"
          options={HAIR_STYLE_OPTIONS}
          value={draft.hairStyle}
          onChange={(v) => set('hairStyle', v)}
        >
          {!hasHairArt(draft.base) && draft.hairStyle !== NO_HAIR_STYLE && (
            <p className="mt-1 text-xs text-textSecondary">
              Hair for this base is still being drawn. Your pick is saved and will appear once it
              arrives.
            </p>
          )}
        </OptionRow>
        {/* Bald draws no hair, so there is nothing to colour. The colour stays in the draft. */}
        {draft.hairStyle !== NO_HAIR_STYLE && (
          <SwatchRow
            legend="Hair colour"
            swatches={HAIR_COLOR_SWATCHES}
            value={draft.hairColor}
            onChange={(v) => set('hairColor', v)}
          />
        )}
      </Card>
    </div>
  );
}
